import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import {
  Calendar, ChevronLeft, ChevronRight, Clock, MapPin, Plane,
  Home, Briefcase, ArrowLeftRight, TrendingUp, User, Heart, Shield,
  AlertCircle, CheckCircle, Timer, Plus, X, XCircle, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonday = (d) => {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date;
};

const iconMap = {
  Plane: Plane, Home: Home, Heart: Heart, Calendar: Calendar,
  Shield: Shield, User: User, Clock: Clock, Briefcase: Briefcase
};

const MySchedule = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [viewRange, setViewRange] = useState('week');
  const [schedules, setSchedules] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashLoading, setDashLoading] = useState(true);

  // PTO/WFH request form state
  const [showPTOForm, setShowPTOForm] = useState(false);
  const [showWFHForm, setShowWFHForm] = useState(false);
  const [ptoTypes, setPtoTypes] = useState([]);
  const [wfhTypes, setWfhTypes] = useState([]);
  const [ptoSubmitting, setPtoSubmitting] = useState(false);
  // Blackout acknowledgment modal state
  const [blackoutAck, setBlackoutAck] = useState({ open: false, conflicts: [], acknowledged: false });
  const [wfhSubmitting, setWfhSubmitting] = useState(false);
  const [ptoForm, setPtoForm] = useState({
    start_date: '', end_date: '', pto_type: 'pto', reason: '',
    is_partial_day: false, start_time: '09:00', end_time: '17:00', exclude_weekends: true
  });
  const [wfhForm, setWfhForm] = useState({
    start_date: '', end_date: '', reason: '', wfh_type: 'flex_bank'
  });

  // Drill-down dialog state
  const [drillDown, setDrillDown] = useState(null); // 'pto' | 'flex_bank' | 'hours' | 'activity' | null
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const getDateRange = useCallback(() => {
    if (viewRange === 'week') {
      const monday = getMonday(currentDate);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return {
        start: formatDate(monday),
        end: formatDate(sunday),
        label: `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
      };
    }
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    return {
      start: formatDate(start),
      end: formatDate(end),
      label: currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    };
  }, [currentDate, viewRange]);

  useEffect(() => {
    const fetchDashboard = async () => {
      setDashLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/my-dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDashboard(res.data);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setDashLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const { start, end } = getDateRange();
      const res = await axios.get(`${API}/my-schedule?start_date=${start}&end_date=${end}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedules(res.data.schedules || []);
    } catch (err) {
      console.error('Failed to load schedule:', err);
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  // Fetch PTO types and WFH types on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };

    axios.get(`${API}/pto-types`, { headers }).then(res => {
      const active = (res.data || []).filter(t => t.is_active).map(t => ({ value: t.value, label: t.display_name }));
      setPtoTypes(active.length ? active : [{ value: 'pto', label: 'PTO' }]);
    }).catch(() => setPtoTypes([{ value: 'pto', label: 'PTO' }, { value: 'sick', label: 'Sick' }]));

    axios.get(`${API}/wfh-types`, { headers }).then(res => {
      setWfhTypes((res.data || []).filter(t => !t.manager_only));
    }).catch(() => setWfhTypes([
      { value: 'flex_bank', display_name: 'Flex Bank', description: 'Use your Flex Bank days', requires_balance: true },
      { value: 'on_call', display_name: 'On-Call WFH', description: 'Work from home while on-call', requires_balance: false }
    ]));
  }, []);

  const refreshDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/my-dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setDashboard(res.data);
    } catch (err) { console.error('Dashboard refresh failed:', err); }
  };

  const submitPTO = async () => {
    setPtoSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const userId = user.id || user.user_id;
      const ptoRes = await axios.post(`${API}/pto-requests`, { ...ptoForm, user_id: userId }, { headers });
      if (ptoRes.data?.has_blackout) {
        toast.success('PTO request submitted — flagged for manager review (blackout period).');
      } else {
        toast.success('PTO request submitted for approval');
      }
      setShowPTOForm(false);
      setBlackoutAck({ open: false, conflicts: [], acknowledged: false });
      setPtoForm({ start_date: '', end_date: '', pto_type: 'pto', reason: '', is_partial_day: false, start_time: '09:00', end_time: '17:00', exclude_weekends: true });
      refreshDashboard();
      fetchSchedule();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit PTO request');
    } finally { setPtoSubmitting(false); }
  };

  const handlePTOSubmit = async (e) => {
    e.preventDefault();
    if (!ptoForm.start_date || !ptoForm.end_date) { toast.error('Please select start and end dates'); return; }
    // Pre-submit blackout check → require explicit acknowledgment if it hits a blackout window
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const check = await axios.get(`${API}/pto-blackouts/check`, {
        headers,
        params: { start_date: ptoForm.start_date, end_date: ptoForm.end_date, pto_type: ptoForm.pto_type },
      });
      if (check.data?.has_blackout) {
        setBlackoutAck({ open: true, conflicts: check.data.blackout_conflicts || [], acknowledged: false });
        return;
      }
    } catch (err) {
      // If the pre-check fails, don't block the user — fall through to submit.
    }
    submitPTO();
  };

  const handleWFHSubmit = async (e) => {
    e.preventDefault();
    if (!wfhForm.start_date || !wfhForm.end_date) { toast.error('Please select start and end dates'); return; }
    setWfhSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const userId = user.id || user.user_id;
      const wfhType = wfhForm.wfh_type || 'flex_bank';
      await axios.post(
        `${API}/wfh-requests?start_date=${wfhForm.start_date}&end_date=${wfhForm.end_date}&user_id=${userId}&reason=${encodeURIComponent(wfhForm.reason || '')}&wfh_type=${wfhType}`,
        {}, { headers }
      );
      const typeLabel = wfhTypes.find(t => t.value === wfhType)?.display_name || wfhType;
      toast.success(`${typeLabel} WFH request submitted`);
      setShowWFHForm(false);
      setWfhForm({ start_date: '', end_date: '', reason: '', wfh_type: 'flex_bank' });
      refreshDashboard();
      fetchSchedule();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit WFH request');
    } finally { setWfhSubmitting(false); }
  };

  const navigate = (dir) => {
    if (viewRange === 'week') {
      const monday = getMonday(currentDate);
      const newMonday = new Date(monday);
      newMonday.setDate(monday.getDate() + (dir * 7));
      setCurrentDate(newMonday);
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + dir, 1));
    }
  };

  const { label } = getDateRange();
  const shiftsByDate = {};
  schedules.forEach(s => {
    if (!shiftsByDate[s.date]) shiftsByDate[s.date] = [];
    shiftsByDate[s.date].push(s);
  });

  const periodHours = schedules
    .filter(s => s.shift_type !== 'pto')
    .reduce((sum, s) => {
      try {
        const st = new Date(`2026-01-01T${s.start_time}`);
        const et = new Date(`2026-01-01T${s.end_time}`);
        let h = (et - st) / 3600000;
        if (h < 0) h += 24;
        return sum + h;
      } catch { return sum + 8; }
    }, 0);

  const getShiftStyle = (type, location) => {
    if (type === 'on_call') return { bg: 'bg-amber-50 border-amber-200 text-amber-900', badge: 'bg-amber-100 text-amber-700 border-amber-300', label: 'On-Call' };
    if (type === 'pto') return { bg: 'bg-violet-50 border-violet-200 text-violet-900', badge: 'bg-violet-100 text-violet-700 border-violet-300', label: 'PTO' };
    if (location === 'wfh' || location === 'remote') return { bg: 'bg-teal-50 border-teal-200 text-teal-900', badge: 'bg-teal-100 text-teal-700 border-teal-300', label: 'WFH' };
    return { bg: 'bg-emerald-50 border-emerald-200 text-emerald-900', badge: 'bg-emerald-100 text-emerald-700 border-emerald-300', label: 'Regular' };
  };

  const fb = dashboard?.flex_bank;
  const pto = dashboard?.pto;
  const stats = dashboard?.year_stats;
  const upcoming = dashboard?.upcoming;
  const swaps = dashboard?.swaps;
  const ptoBalances = pto?.by_type ? Object.entries(pto.by_type) : [];

  // Open a drill-down dialog and fetch the matching detail records for the logged-in user
  const openDrillDown = async (kind) => {
    setDrillDown(kind);
    setDrillData(null);
    setDrillLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const userId = user?.id || user?.user_id;
      const year = stats?.year || new Date().getFullYear();
      const yearStart = `${year}-01-01`;
      const yearEnd = `${year}-12-31`;

      if (kind === 'pto') {
        const res = await axios.get(`${API}/pto-requests?employee_id=${userId}`, { headers });
        const requests = (res.data || []).filter(r => r.start_date >= yearStart && r.start_date <= yearEnd);
        setDrillData({ requests });
      } else if (kind === 'flex_bank') {
        const res = await axios.get(`${API}/wfh-requests?user_id=${userId}`, { headers });
        const requests = (res.data || []).filter(r => r.start_date >= yearStart && r.start_date <= yearEnd);
        setDrillData({ requests });
      } else if (kind === 'hours') {
        const res = await axios.get(`${API}/my-schedule?start_date=${yearStart}&end_date=${yearEnd}`, { headers });
        setDrillData({ schedules: res.data?.schedules || [] });
      } else if (kind === 'activity') {
        const [ptoRes, wfhRes] = await Promise.all([
          axios.get(`${API}/pto-requests?employee_id=${userId}&status=pending`, { headers }),
          axios.get(`${API}/wfh-requests?user_id=${userId}&status=pending`, { headers }),
        ]);
        setDrillData({
          pending_pto: ptoRes.data || [],
          pending_wfh: wfhRes.data || [],
          pending_swaps_count: swaps?.pending || 0,
          completed_swaps_count: swaps?.completed || 0,
        });
      }
    } catch (err) {
      console.error('Drill-down load failed:', err);
      toast.error('Failed to load details');
    } finally {
      setDrillLoading(false);
    }
  };

  const closeDrillDown = () => { setDrillDown(null); setDrillData(null); };

  const formatPrettyDate = (s) => {
    if (!s) return '';
    const [y, m, d] = s.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const statusBadgeClass = (status) => ({
    approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    denied: 'bg-rose-100 text-rose-700 border-rose-200',
    cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
  }[status] || 'bg-gray-100 text-gray-600 border-gray-200');

  return (
    <div className="space-y-6" data-testid="my-schedule-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Schedule</h1>
        <div className="text-gray-500 mt-0.5 flex items-center flex-wrap gap-x-1">
          {dashboard?.user?.name || user?.name || user?.username}
          {dashboard?.user?.job_title && <span className="text-gray-400"> · {dashboard.user.job_title}</span>}
          {dashboard?.user?.role && <Badge variant="outline" className="ml-2 text-xs capitalize">{dashboard.user.role}</Badge>}
        </div>
      </div>

      {dashLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Card key={i}><CardContent className="p-4 h-24 animate-pulse bg-gray-50" /></Card>)}
        </div>
      ) : dashboard && (
        <>
          {/* ===== SECTION 1: KEY BALANCES ===== */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* PTO Used */}
            <Card
              className="border-l-4 border-l-violet-500 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 focus:ring-2 focus:ring-violet-200"
              role="button"
              tabIndex={0}
              onClick={() => openDrillDown('pto')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrillDown('pto'); } }}
              data-testid="pto-summary-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">PTO Used</span>
                  <Plane className="h-4 w-4 text-violet-400" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{pto?.total_used ?? 0}<span className="text-sm font-normal text-gray-400 ml-1">days</span></div>
                {(pto?.total_upcoming ?? 0) > 0 && <div className="text-xs text-green-600 mt-1 flex items-center gap-1"><CheckCircle className="h-3 w-3"/>{pto.total_upcoming}d approved upcoming</div>}
                {pto?.total_pending > 0 && <div className="text-xs text-amber-600 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3"/>{pto.total_pending}d pending approval</div>}
                <div className="text-[10px] text-violet-500 mt-2 font-medium">Click for details →</div>
              </CardContent>
            </Card>

            {/* Flex Bank / WFH */}
            <Card
              className="border-l-4 border-l-teal-500 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 focus:ring-2 focus:ring-teal-200"
              role="button"
              tabIndex={0}
              onClick={() => openDrillDown('flex_bank')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrillDown('flex_bank'); } }}
              data-testid="flex-bank-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Flex Bank</span>
                  <Home className="h-4 w-4 text-teal-400" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{fb?.balance ?? 0}<span className="text-sm font-normal text-gray-400 ml-1">/ {fb?.total ?? 15}</span></div>
                <Progress value={fb?.total ? ((fb.used / fb.total) * 100) : 0} className="h-1.5 mt-2" />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>{fb?.used ?? 0} flex used</span>
                  {fb?.pending > 0 && <span className="text-amber-600">{fb.pending} pending</span>}
                  <span>{fb?.balance ?? 0} left</span>
                </div>
                {fb?.total_wfh_approved > 0 && (
                  <div className="text-[10px] text-gray-400 mt-1">{fb.total_wfh_approved} WFH requests approved this year</div>
                )}
                <div className="text-[10px] text-teal-500 mt-2 font-medium">Click for details →</div>
              </CardContent>
            </Card>

            {/* Work Hours */}
            <Card
              className="border-l-4 border-l-blue-500 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 focus:ring-2 focus:ring-blue-200"
              role="button"
              tabIndex={0}
              onClick={() => openDrillDown('hours')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrillDown('hours'); } }}
              data-testid="year-hours-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hours ({stats?.year})</span>
                  <Timer className="h-4 w-4 text-blue-400" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{stats?.regular_hours ?? 0}<span className="text-sm font-normal text-gray-400 ml-1">h worked</span></div>
                <div className="text-xs text-gray-400 mt-1">{stats?.regular_shifts ?? 0} shifts · {stats?.wfh_shifts ?? 0} WFH</div>
                {stats?.oncall_hours > 0 && <div className="text-xs text-amber-600 mt-0.5">+{stats.oncall_hours}h on-call ({stats.oncall_shifts} shifts)</div>}
                <div className="text-[10px] text-blue-500 mt-2 font-medium">Click for details →</div>
              </CardContent>
            </Card>

            {/* Pending Actions */}
            <Card
              className="border-l-4 border-l-orange-500 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 focus:ring-2 focus:ring-orange-200"
              role="button"
              tabIndex={0}
              onClick={() => openDrillDown('activity')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrillDown('activity'); } }}
              data-testid="pending-items-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Activity</span>
                  <ArrowLeftRight className="h-4 w-4 text-orange-400" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{(pto?.total_pending ?? 0) + (swaps?.pending ?? 0)}<span className="text-sm font-normal text-gray-400 ml-1">pending</span></div>
                <div className="text-xs text-gray-400 mt-1">
                  {pto?.total_pending ?? 0} PTO · {swaps?.pending ?? 0} swaps
                </div>
                {swaps?.completed > 0 && <div className="text-xs text-green-600 mt-0.5 flex items-center gap-1"><CheckCircle className="h-3 w-3"/>{swaps.completed} swaps done</div>}
                <div className="text-[10px] text-orange-500 mt-2 font-medium">Click for details →</div>
              </CardContent>
            </Card>
          </div>

          {/* ===== SECTION 2: PTO BREAKDOWN ===== */}
          {ptoBalances.length > 0 && (
            <Card>
              <CardHeader className="pb-2 pt-3 px-5">
                <CardTitle className="text-sm font-semibold text-gray-700">PTO Balances — {stats?.year}</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {ptoBalances.map(([key, val]) => {
                    const IconComp = iconMap[val.icon] || Calendar;
                    const hasActivity = val.used > 0 || val.approved_upcoming > 0 || val.pending > 0;
                    return (
                      <div key={key} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border" data-testid={`pto-balance-${key}`}>
                        <div className="mt-0.5">
                          <IconComp className="h-4 w-4 text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-800">{val.display_name}</div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                            <span className="text-sm font-bold text-gray-900">{val.used}d <span className="text-xs font-normal text-gray-400">used</span></span>
                            {val.approved_upcoming > 0 && (
                              <span className="text-sm font-bold text-green-700">{val.approved_upcoming}d <span className="text-xs font-normal text-green-500">upcoming</span></span>
                            )}
                          </div>
                          {val.pending > 0 && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0 mt-1">{val.pending}d pending</Badge>}
                          {!hasActivity && <div className="text-[10px] text-gray-400 mt-1">No activity</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ===== SECTION 3: WFH BREAKDOWN ===== */}
          {fb && (fb.used > 0 || fb.total_wfh_approved > 0 || fb.wfh_breakdown) && (
            <Card>
              <CardHeader className="pb-2 pt-3 px-5">
                <CardTitle className="text-sm font-semibold text-gray-700">WFH Activity — {stats?.year}</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 rounded-lg bg-teal-50 border border-teal-100">
                    <div className="text-xs text-teal-600 font-medium">Flex Bank</div>
                    <div className="text-lg font-bold text-teal-900 mt-0.5">{fb.wfh_breakdown?.flex_bank ?? 0}d</div>
                    <div className="text-[10px] text-teal-500">Deducted from balance</div>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 border">
                    <div className="text-xs text-gray-600 font-medium">Legacy WFH</div>
                    <div className="text-lg font-bold text-gray-900 mt-0.5">{fb.wfh_breakdown?.legacy ?? 0}d</div>
                    <div className="text-[10px] text-gray-400">Pre-flex system</div>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                    <div className="text-xs text-amber-600 font-medium">On-Call WFH</div>
                    <div className="text-lg font-bold text-amber-900 mt-0.5">{fb.wfh_breakdown?.on_call ?? 0}d</div>
                    <div className="text-[10px] text-amber-500">No balance deduction</div>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                    <div className="text-xs text-blue-600 font-medium">Business Directed</div>
                    <div className="text-lg font-bold text-blue-900 mt-0.5">{fb.wfh_breakdown?.business_directed ?? 0}d</div>
                    <div className="text-[10px] text-blue-500">No balance deduction</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ===== SECTION 4: UPCOMING ===== */}
          {(upcoming?.shifts?.length > 0 || upcoming?.pto?.length > 0 || upcoming?.wfh?.length > 0) && (
            <Card>
              <CardHeader className="pb-2 pt-3 px-5">
                <CardTitle className="text-sm font-semibold text-gray-700">Upcoming — Next 14 Days</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4 space-y-1.5">
                {upcoming.pto?.map((p, i) => (
                  <div key={`pto-${i}`} className="flex items-center justify-between p-2.5 rounded-lg bg-violet-50 border border-violet-100 text-sm">
                    <div className="flex items-center gap-2">
                      <Plane className="h-3.5 w-3.5 text-violet-500" />
                      <span className="font-medium text-violet-900">{p.pto_type_display || 'PTO'}</span>
                      <Badge variant="outline" className={`text-[10px] ${p.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{p.status}</Badge>
                    </div>
                    <span className="text-xs text-violet-600">{p.start_date} → {p.end_date}</span>
                  </div>
                ))}
                {upcoming.wfh?.map((w, i) => (
                  <div key={`wfh-${i}`} className="flex items-center justify-between p-2.5 rounded-lg bg-teal-50 border border-teal-100 text-sm">
                    <div className="flex items-center gap-2">
                      <Home className="h-3.5 w-3.5 text-teal-500" />
                      <span className="font-medium text-teal-900">Work From Home</span>
                      <Badge variant="outline" className={`text-[10px] ${w.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{w.status}</Badge>
                    </div>
                    <span className="text-xs text-teal-600">{w.start_date} → {w.end_date}</span>
                  </div>
                ))}
                {upcoming.shifts?.length > 0 && (
                  <>
                    {(upcoming.pto?.length > 0 || upcoming.wfh?.length > 0) && <Separator className="my-2" />}
                    {upcoming.shifts.slice(0, 6).map((s, i) => {
                      const style = getShiftStyle(s.shift_type, s.work_location);
                      return (
                        <div key={`shift-${i}`} className={`flex items-center justify-between p-2.5 rounded-lg border text-sm ${style.bg}`}>
                          <div className="flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 opacity-60" />
                            <span className="font-medium">{s.start_time} - {s.end_time}</span>
                            <Badge variant="outline" className={`text-[10px] ${style.badge}`}>{style.label}</Badge>
                          </div>
                          <span className="text-xs opacity-70">{new Date(s.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                        </div>
                      );
                    })}
                  </>
                )}
              </CardContent>
            </Card>
          )}
          {/* ===== SECTION 5: REQUEST ACTIONS ===== */}
          <div className="flex gap-3" data-testid="request-actions">
            <Button
              onClick={() => { setShowPTOForm(!showPTOForm); setShowWFHForm(false); }}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="my-schedule-request-pto-btn"
            >
              <Plane className="h-4 w-4 mr-2" />
              Request PTO
            </Button>
            <Button
              onClick={() => { setShowWFHForm(!showWFHForm); setShowPTOForm(false); }}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="my-schedule-request-wfh-btn"
            >
              <Home className="h-4 w-4 mr-2" />
              Request WFH
            </Button>
          </div>

          {/* PTO Request Form */}
          {showPTOForm && (
            <Card className="border-violet-200" data-testid="pto-request-form">
              <CardHeader className="pb-2 pt-3 px-5 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold text-violet-800 flex items-center gap-2">
                  <Plane className="h-4 w-4" /> Request Time Off
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowPTOForm(false)} data-testid="close-pto-form"><X className="h-4 w-4" /></Button>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                <form onSubmit={handlePTOSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Start Date *</label>
                      <Input type="date" value={ptoForm.start_date} onChange={e => setPtoForm({...ptoForm, start_date: e.target.value})} required data-testid="pto-start-date" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">End Date *</label>
                      <Input type="date" value={ptoForm.end_date} onChange={e => setPtoForm({...ptoForm, end_date: e.target.value})} required data-testid="pto-end-date" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">PTO Type *</label>
                    <Select value={ptoForm.pto_type} onValueChange={v => setPtoForm({...ptoForm, pto_type: v})}>
                      <SelectTrigger data-testid="pto-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ptoTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
                    <Input type="text" placeholder="Optional reason" value={ptoForm.reason} onChange={e => setPtoForm({...ptoForm, reason: e.target.value})} data-testid="pto-reason" />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="my_partial_day" checked={ptoForm.is_partial_day} onChange={e => setPtoForm({...ptoForm, is_partial_day: e.target.checked})} className="h-4 w-4 rounded border-gray-300" />
                      <label htmlFor="my_partial_day" className="text-sm">Partial day</label>
                    </div>
                    {ptoForm.is_partial_day && (
                      <div className="grid grid-cols-2 gap-4 pl-6">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                          <Input type="time" value={ptoForm.start_time} onChange={e => setPtoForm({...ptoForm, start_time: e.target.value})} data-testid="pto-start-time" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">End Time</label>
                          <Input type="time" value={ptoForm.end_time} onChange={e => setPtoForm({...ptoForm, end_time: e.target.value})} data-testid="pto-end-time" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="my_excl_weekends" checked={ptoForm.exclude_weekends} onChange={e => setPtoForm({...ptoForm, exclude_weekends: e.target.checked})} className="h-4 w-4 rounded border-gray-300" />
                    <label htmlFor="my_excl_weekends" className="text-sm">Exclude weekends</label>
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" disabled={ptoSubmitting} className="bg-violet-600 hover:bg-violet-700" data-testid="pto-submit-btn">
                      {ptoSubmitting ? 'Submitting...' : 'Submit PTO Request'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowPTOForm(false)}>Cancel</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* WFH Request Form */}
          {showWFHForm && (
            <Card className="border-teal-200" data-testid="wfh-request-form">
              <CardHeader className="pb-2 pt-3 px-5 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold text-teal-800 flex items-center gap-2">
                  <Home className="h-4 w-4" /> Request Work From Home
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowWFHForm(false)} data-testid="close-wfh-form"><X className="h-4 w-4" /></Button>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                {fb && wfhForm.wfh_type === 'flex_bank' && (
                  <div className="mb-4 p-3 rounded-lg bg-teal-50 border border-teal-100 text-sm">
                    <span className="font-medium text-teal-800">Flex Bank Balance:</span>{' '}
                    <span className="text-teal-900 font-bold">{fb.balance}</span>
                    <span className="text-teal-600"> / {fb.total} days remaining</span>
                  </div>
                )}
                <form onSubmit={handleWFHSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">WFH Type *</label>
                    <Select value={wfhForm.wfh_type} onValueChange={v => setWfhForm({...wfhForm, wfh_type: v})}>
                      <SelectTrigger data-testid="wfh-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {wfhTypes.map(t => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.display_name}
                            {t.requires_balance && ' (uses balance)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Start Date *</label>
                      <Input type="date" value={wfhForm.start_date} onChange={e => setWfhForm({...wfhForm, start_date: e.target.value})} required data-testid="wfh-start-date" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">End Date *</label>
                      <Input type="date" value={wfhForm.end_date} onChange={e => setWfhForm({...wfhForm, end_date: e.target.value})} required data-testid="wfh-end-date" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
                    <Input type="text" placeholder="Optional reason" value={wfhForm.reason} onChange={e => setWfhForm({...wfhForm, reason: e.target.value})} data-testid="wfh-reason" />
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" disabled={wfhSubmitting} className="bg-teal-600 hover:bg-teal-700" data-testid="wfh-submit-btn">
                      {wfhSubmitting ? 'Submitting...' : 'Submit WFH Request'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowWFHForm(false)}>Cancel</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ===== SECTION 6: SCHEDULE BROWSER ===== */}
      <Separator />
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-800">Schedule</h2>
        <Select value={viewRange} onValueChange={setViewRange}>
          <SelectTrigger className="w-28" data-testid="my-schedule-range-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">Week</SelectItem>
            <SelectItem value="month">Month</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between bg-white rounded-lg border p-3">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="my-schedule-prev">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="font-semibold text-gray-900">{label}</span>
          {periodHours > 0 && <span className="text-xs text-gray-400 ml-2">{periodHours.toFixed(1)}h</span>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>Today</Button>
          <Button variant="outline" size="sm" onClick={() => navigate(1)} data-testid="my-schedule-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600"></div>
        </div>
      ) : Object.keys(shiftsByDate).length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 font-medium text-sm">No shifts scheduled</p>
            <p className="text-gray-400 text-xs mt-1">Nothing here for this period</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {Object.entries(shiftsByDate).map(([dateStr, shifts]) => {
            const d = new Date(dateStr + 'T12:00:00');
            const isToday = dateStr === formatDate(new Date());
            return (
              <Card key={dateStr} className={isToday ? 'ring-2 ring-blue-400 ring-offset-1' : ''}>
                <CardHeader className="pb-2 pt-3 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${isToday ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                        {d.getDate()}
                      </span>
                      <span className="font-semibold text-sm text-gray-900">{d.toLocaleDateString('en-US', { weekday: 'long' })}</span>
                      <span className="text-gray-400 text-xs">{d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      {isToday && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Today</Badge>}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-3 space-y-1.5">
                  {shifts.map((shift, idx) => {
                    const style = getShiftStyle(shift.shift_type, shift.work_location);
                    return (
                      <div key={idx} className={`p-2.5 rounded-lg border ${style.bg}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <span className="font-medium text-sm">{shift.start_time} - {shift.end_time}</span>
                            <Badge variant="outline" className={`text-[10px] ${style.badge}`}>{style.label}</Badge>
                            {shift.template_name && <span className="text-[11px] opacity-60">{shift.template_name}</span>}
                          </div>
                          <div className="flex items-center gap-1 text-[11px] opacity-50">
                            <MapPin className="h-3 w-3" />
                            {shift.work_location || 'office'}
                          </div>
                        </div>
                        {shift.notes && <p className="text-[11px] mt-1 opacity-60">{shift.notes}</p>}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Drill-down dialog for stat cards */}
      {/* PTO Blackout acknowledgment modal */}
      <Dialog open={blackoutAck.open} onOpenChange={(open) => { if (!open) setBlackoutAck({ open: false, conflicts: [], acknowledged: false }); }}>
        <DialogContent className="max-w-lg" data-testid="blackout-ack-dialog">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-rose-100">
              <AlertTriangle className="h-7 w-7 text-rose-600" />
            </div>
            <DialogTitle className="text-center text-rose-700 text-xl">Heads up — PTO Blackout Period</DialogTitle>
            <DialogDescription className="text-center">
              The dates you selected fall within a PTO blackout window. Requests during this period are less likely to be approved.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 space-y-2" data-testid="blackout-ack-conflicts">
            {blackoutAck.conflicts.map((b) => (
              <div key={b.id} className="flex items-start gap-2 text-sm text-rose-800">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-rose-500" />
                <span><span className="font-semibold">{b.label}</span>{b.start_date ? ` (${b.start_date}${b.end_date && b.end_date !== b.start_date ? ` – ${b.end_date}` : ''})` : ''}</span>
              </div>
            ))}
          </div>

          <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:bg-gray-50 transition-colors">
            <Checkbox
              checked={blackoutAck.acknowledged}
              onCheckedChange={(v) => setBlackoutAck((s) => ({ ...s, acknowledged: !!v }))}
              data-testid="blackout-ack-checkbox"
              className="mt-0.5"
            />
            <span className="text-sm text-gray-700">
              I understand these dates are within a PTO blackout period and my request may not be approved.
            </span>
          </label>

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setBlackoutAck({ open: false, conflicts: [], acknowledged: false })}
              data-testid="blackout-ack-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700"
              disabled={!blackoutAck.acknowledged || ptoSubmitting}
              onClick={submitPTO}
              data-testid="blackout-ack-submit"
            >
              {ptoSubmitting ? 'Submitting...' : 'Submit request anyway'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!drillDown} onOpenChange={(open) => { if (!open) closeDrillDown(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="drill-down-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {drillDown === 'pto' && (<><Plane className="h-5 w-5 text-violet-500" /> Your PTO Requests ({stats?.year})</>)}
              {drillDown === 'flex_bank' && (<><Home className="h-5 w-5 text-teal-500" /> Your WFH / Flex Bank Requests ({stats?.year})</>)}
              {drillDown === 'hours' && (<><Timer className="h-5 w-5 text-blue-500" /> Your Hours Breakdown ({stats?.year})</>)}
              {drillDown === 'activity' && (<><ArrowLeftRight className="h-5 w-5 text-orange-500" /> Your Pending Activity</>)}
            </DialogTitle>
            <DialogDescription>
              {drillDown === 'pto' && 'All PTO requests you have submitted this year.'}
              {drillDown === 'flex_bank' && 'All Work-From-Home and Flex Bank requests you have submitted this year.'}
              {drillDown === 'hours' && 'Shifts and hours breakdown for the current year.'}
              {drillDown === 'activity' && 'Pending requests and swaps awaiting action.'}
            </DialogDescription>
          </DialogHeader>

          {drillLoading && (
            <div className="py-12 text-center text-sm text-gray-500">Loading details...</div>
          )}

          {!drillLoading && drillDown === 'pto' && drillData && (
            <PtoDrillContent requests={drillData.requests} pto={pto} ptoTypes={ptoTypes} formatPrettyDate={formatPrettyDate} statusBadgeClass={statusBadgeClass} />
          )}

          {!drillLoading && drillDown === 'flex_bank' && drillData && (
            <WfhDrillContent requests={drillData.requests} fb={fb} wfhTypes={wfhTypes} formatPrettyDate={formatPrettyDate} statusBadgeClass={statusBadgeClass} />
          )}

          {!drillLoading && drillDown === 'hours' && drillData && (
            <HoursDrillContent schedules={drillData.schedules} stats={stats} />
          )}

          {!drillLoading && drillDown === 'activity' && drillData && (
            <ActivityDrillContent data={drillData} formatPrettyDate={formatPrettyDate} statusBadgeClass={statusBadgeClass} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===== Drill-down content components =====
const PtoDrillContent = ({ requests, pto, ptoTypes, formatPrettyDate, statusBadgeClass }) => {
  const byStatus = { approved: [], pending: [], denied: [], cancelled: [] };
  requests.forEach((r) => { (byStatus[r.status] || byStatus.cancelled).push(r); });
  const sortedByStart = (arr) => [...arr].sort((a, b) => (a.start_date < b.start_date ? 1 : -1));
  const labelForType = (val) => ptoTypes.find((t) => t.value === val)?.label || val;

  return (
    <div className="space-y-4" data-testid="pto-drill-content">
      {pto?.by_type && Object.keys(pto.by_type).length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">By Type</div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(pto.by_type).map(([type, info]) => {
              const used = typeof info === 'object' ? (info?.used ?? 0) : (info || 0);
              const upcoming = typeof info === 'object' ? (info?.approved_upcoming ?? 0) : 0;
              const pending = typeof info === 'object' ? (info?.pending ?? 0) : 0;
              const display = (typeof info === 'object' && info?.display_name) ? info.display_name : labelForType(type);
              return (
                <div key={type} className="rounded-md border border-gray-200 px-3 py-2 flex items-center justify-between" data-testid={`pto-type-${type}`}>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-700">{display}</span>
                    {(upcoming > 0 || pending > 0) && (
                      <span className="text-[10px] text-gray-400">
                        {upcoming > 0 ? `${upcoming}d upcoming` : ''}{upcoming > 0 && pending > 0 ? ' · ' : ''}{pending > 0 ? `${pending}d pending` : ''}
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{used}d</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {['pending', 'approved', 'denied', 'cancelled'].map((s) =>
        byStatus[s].length > 0 ? (
          <div key={s}>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{s} ({byStatus[s].length})</div>
            <div className="space-y-2">
              {sortedByStart(byStatus[s]).map((r) => (
                <div key={r.id} className="rounded-lg border border-gray-200 p-3 flex items-start justify-between gap-3" data-testid={`pto-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{formatPrettyDate(r.start_date)}{r.end_date !== r.start_date ? ` → ${formatPrettyDate(r.end_date)}` : ''}</span>
                      <Badge variant="outline" className={statusBadgeClass(r.status)}>{r.status}</Badge>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {r.pto_type_display || labelForType(r.pto_type)} · {r.days_requested || 1} day{(r.days_requested || 1) > 1 ? 's' : ''}
                      {r.is_partial_day && ' · partial day'}
                    </div>
                    {r.reason && <div className="text-xs text-gray-600 mt-1 italic">"{r.reason}"</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null
      )}

      {requests.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-500">No PTO requests this year yet.</div>
      )}
    </div>
  );
};

const WfhDrillContent = ({ requests, fb, wfhTypes, formatPrettyDate, statusBadgeClass }) => {
  const byStatus = { approved: [], pending: [], denied: [], cancelled: [] };
  requests.forEach((r) => { (byStatus[r.status] || byStatus.cancelled).push(r); });
  const sortedByStart = (arr) => [...arr].sort((a, b) => (a.start_date < b.start_date ? 1 : -1));
  const labelForType = (val) => wfhTypes.find((t) => t.value === val)?.display_name || (val || 'WFH').replace('_', ' ');

  return (
    <div className="space-y-4" data-testid="flex-bank-drill-content">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="rounded-md bg-teal-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-teal-600 font-semibold">Balance</div>
          <div className="text-lg font-bold text-teal-900">{fb?.balance ?? 0} <span className="text-xs font-normal text-teal-700">/ {fb?.total ?? 15}</span></div>
        </div>
        <div className="rounded-md bg-gray-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Flex Used</div>
          <div className="text-lg font-bold text-gray-900">{fb?.used ?? 0}d</div>
        </div>
        <div className="rounded-md bg-amber-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-amber-600 font-semibold">Pending</div>
          <div className="text-lg font-bold text-amber-900">{fb?.pending ?? 0}d</div>
        </div>
        <div className="rounded-md bg-gray-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Approved</div>
          <div className="text-lg font-bold text-gray-900">{fb?.total_wfh_approved ?? 0}</div>
        </div>
      </div>

      {fb?.wfh_breakdown && (
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">By Type (days)</div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(fb.wfh_breakdown).map(([k, v]) => {
              const days = typeof v === 'object' ? (v?.used ?? v?.days ?? 0) : (v || 0);
              return (
                <div key={k} className="rounded-md border border-gray-200 px-3 py-2 flex items-center justify-between">
                  <span className="text-sm text-gray-700">{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  <span className="text-sm font-semibold text-gray-900">{days}d</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {['pending', 'approved', 'denied', 'cancelled'].map((s) =>
        byStatus[s].length > 0 ? (
          <div key={s}>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{s} ({byStatus[s].length})</div>
            <div className="space-y-2">
              {sortedByStart(byStatus[s]).map((r) => (
                <div key={r.id} className="rounded-lg border border-gray-200 p-3" data-testid={`wfh-row-${r.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{formatPrettyDate(r.start_date)}{r.end_date !== r.start_date ? ` → ${formatPrettyDate(r.end_date)}` : ''}</span>
                    <Badge variant="outline" className={statusBadgeClass(r.status)}>{r.status}</Badge>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {labelForType(r.wfh_type)} · {r.days_requested ?? 1} day{(r.days_requested ?? 1) > 1 ? 's' : ''}
                  </div>
                  {r.reason && <div className="text-xs text-gray-600 mt-1 italic">"{r.reason}"</div>}
                </div>
              ))}
            </div>
          </div>
        ) : null
      )}

      {requests.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-500">No WFH requests this year yet.</div>
      )}
    </div>
  );
};

const HoursDrillContent = ({ schedules, stats }) => {
  // Group by month
  const byMonth = {};
  schedules.forEach((s) => {
    if (!s.date) return;
    const monthKey = s.date.slice(0, 7);
    if (!byMonth[monthKey]) byMonth[monthKey] = { regular: 0, oncall: 0, wfh: 0, count: 0 };
    byMonth[monthKey].count++;
    const hours = s.hours || 8;
    if (s.shift_type === 'on_call') byMonth[monthKey].oncall += hours;
    else if (s.shift_type === 'wfh') byMonth[monthKey].wfh += hours;
    else byMonth[monthKey].regular += hours;
  });

  const months = Object.keys(byMonth).sort();
  const monthLabel = (k) => {
    const [y, m] = k.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-4" data-testid="hours-drill-content">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-md bg-blue-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold">Regular</div>
          <div className="text-lg font-bold text-blue-900">{stats?.regular_hours ?? 0}h</div>
          <div className="text-[10px] text-blue-700">{stats?.regular_shifts ?? 0} shifts</div>
        </div>
        <div className="rounded-md bg-amber-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-amber-600 font-semibold">On-Call</div>
          <div className="text-lg font-bold text-amber-900">{stats?.oncall_hours ?? 0}h</div>
          <div className="text-[10px] text-amber-700">{stats?.oncall_shifts ?? 0} shifts</div>
        </div>
        <div className="rounded-md bg-teal-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-teal-600 font-semibold">WFH</div>
          <div className="text-lg font-bold text-teal-900">{stats?.wfh_shifts ?? 0}</div>
          <div className="text-[10px] text-teal-700">shifts</div>
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Monthly Breakdown</div>
        {months.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-4">No shifts logged this year yet.</div>
        ) : (
          <div className="space-y-1">
            {months.map((m) => {
              const d = byMonth[m];
              const total = d.regular + d.oncall + d.wfh;
              return (
                <div key={m} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-gray-50" data-testid={`hours-month-${m}`}>
                  <div className="w-24 text-sm font-medium text-gray-900">{monthLabel(m)}</div>
                  <div className="flex-1 flex h-2 rounded-full overflow-hidden bg-gray-100">
                    {d.regular > 0 && <div className="bg-blue-500" style={{ width: `${(d.regular / total) * 100}%` }} />}
                    {d.oncall > 0 && <div className="bg-amber-500" style={{ width: `${(d.oncall / total) * 100}%` }} />}
                    {d.wfh > 0 && <div className="bg-teal-500" style={{ width: `${(d.wfh / total) * 100}%` }} />}
                  </div>
                  <div className="text-xs text-gray-600 w-24 text-right">{d.count} shifts · {total.toFixed(0)}h</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const ActivityDrillContent = ({ data, formatPrettyDate, statusBadgeClass }) => (
  <div className="space-y-4" data-testid="activity-drill-content">
    <div className="grid grid-cols-2 gap-2">
      <div className="rounded-md bg-amber-50 px-3 py-2">
        <div className="text-[10px] uppercase tracking-wide text-amber-600 font-semibold">Pending PTO</div>
        <div className="text-lg font-bold text-amber-900">{data.pending_pto.length}</div>
      </div>
      <div className="rounded-md bg-amber-50 px-3 py-2">
        <div className="text-[10px] uppercase tracking-wide text-amber-600 font-semibold">Pending WFH</div>
        <div className="text-lg font-bold text-amber-900">{data.pending_wfh.length}</div>
      </div>
      <div className="rounded-md bg-orange-50 px-3 py-2">
        <div className="text-[10px] uppercase tracking-wide text-orange-600 font-semibold">Pending Swaps</div>
        <div className="text-lg font-bold text-orange-900">{data.pending_swaps_count}</div>
      </div>
      <div className="rounded-md bg-emerald-50 px-3 py-2">
        <div className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">Completed Swaps</div>
        <div className="text-lg font-bold text-emerald-900">{data.completed_swaps_count}</div>
      </div>
    </div>

    {data.pending_pto.length > 0 && (
      <div>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Pending PTO Requests</div>
        <div className="space-y-2">
          {data.pending_pto.map((r) => (
            <div key={r.id} className="rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid={`activity-pto-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Plane className="h-3.5 w-3.5 text-violet-500" />
                <span className="font-medium text-gray-900">{formatPrettyDate(r.start_date)}{r.end_date !== r.start_date ? ` → ${formatPrettyDate(r.end_date)}` : ''}</span>
                <Badge variant="outline" className={statusBadgeClass(r.status)}>{r.status}</Badge>
              </div>
              <div className="text-xs text-gray-500 mt-1">{r.pto_type_display || r.pto_type} · {r.days_requested || 1}d</div>
              {r.reason && <div className="text-xs text-gray-600 mt-1 italic">"{r.reason}"</div>}
            </div>
          ))}
        </div>
      </div>
    )}

    {data.pending_wfh.length > 0 && (
      <div>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Pending WFH Requests</div>
        <div className="space-y-2">
          {data.pending_wfh.map((r) => (
            <div key={r.id} className="rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid={`activity-wfh-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Home className="h-3.5 w-3.5 text-teal-500" />
                <span className="font-medium text-gray-900">{formatPrettyDate(r.start_date)}{r.end_date !== r.start_date ? ` → ${formatPrettyDate(r.end_date)}` : ''}</span>
                <Badge variant="outline" className={statusBadgeClass(r.status)}>{r.status}</Badge>
              </div>
              <div className="text-xs text-gray-500 mt-1">{(r.wfh_type || 'wfh').replace('_', ' ')} · {r.days_requested ?? 1}d</div>
              {r.reason && <div className="text-xs text-gray-600 mt-1 italic">"{r.reason}"</div>}
            </div>
          ))}
        </div>
      </div>
    )}

    {data.pending_pto.length === 0 && data.pending_wfh.length === 0 && data.pending_swaps_count === 0 && (
      <div className="text-center py-8 text-sm text-gray-500">Nothing pending — you're all caught up!</div>
    )}
  </div>
);

export default MySchedule;
